import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  alpha,
  useTheme
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon,
  Camera as CameraIcon
} from '@mui/icons-material';

interface MacroImage {
  id: string;
  mappingId: string;
  imageUrl: string;
  imageName: string;
  uploadDate: string;
orderIndex: number;
}

interface MacroImageGalleryProps {
  apiBaseUrl: string;
  macroImages: MacroImage[];
  selectedMacroImage: MacroImage | null;
  isDragActive: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onSelectImage: (image: MacroImage) => void;
  onUploadImage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onReplaceImage: (event: React.ChangeEvent<HTMLInputElement>, image: MacroImage) => void;
  onDeleteImage: (image: MacroImage) => void;
  onOpenCamera: () => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

const MacroImageGallery: React.FC<MacroImageGalleryProps> = ({
  apiBaseUrl,
  macroImages,
  selectedMacroImage,
  isDragActive,
  fileInputRef,
  onSelectImage,
  onUploadImage,
  onReplaceImage,
  onDeleteImage,
  onOpenCamera,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop
}) => {
  const theme = useTheme();

  return (
    <Box 
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      sx={{ 
        display: 'flex', 
        gap: 1, 
        mb: 2, 
        overflowX: 'auto', 
        pb: 1,
        position: 'relative',
        borderRadius: 1,
        border: isDragActive ? 
          `2px dashed ${theme.palette.primary.main}` : 
          'none',
        p: isDragActive ? 1.5 : 0,
        transition: 'all 0.2s ease',
        ...(isDragActive && {
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
        })
      }}
    >
      {isDragActive && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 1,
          borderRadius: 1,
        }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
            Drop image here
          </Typography>
        </Box>
      )}
      
      {macroImages.map(image => (
        <Paper
          key={image.id}
          sx={{
            width: 100,
            height: 100,
            position: 'relative',
            cursor: 'pointer',
            border: selectedMacroImage?.id === image.id
              ? `2px solid ${theme.palette.primary.main}`
              : '2px solid transparent',
            overflow: 'hidden',
            '&:hover .macro-actions': {
              opacity: 1
            }
          }}
          onClick={() => onSelectImage(image)}
        >
          <img
            src={`${apiBaseUrl}${image.imageUrl}`}
            alt={image.imageName || 'Macro image'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          {selectedMacroImage?.id === image.id && (
            <Box sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.7),
              color: 'white',
              fontSize: '0.7rem',
              p: 0.5,
              textAlign: 'center'
            }}>
              Active
            </Box>
          )}
          
          {/* Macro image action buttons */}
          <Box 
            className="macro-actions"
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s ease'
            }}
          >
            <IconButton
              size="small"
              component="label"
              sx={{
                backgroundColor: alpha(theme.palette.warning.main, 0.9),
                color: '#fff',
                width: 20,
                height: 20,
                '&:hover': {
                  backgroundColor: theme.palette.warning.main
                }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <EditIcon fontSize="small" />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => onReplaceImage(e, image)}
              />
            </IconButton>
            
            <IconButton
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.9),
                color: '#fff',
                width: 20,
                height: 20,
                '&:hover': {
                  backgroundColor: theme.palette.error.main
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteImage(image);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      ))}
      
      {/* Upload button - updated with camera option */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Paper
          sx={{
            width: 100,
            height: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: '2px dashed',
            borderColor: 'divider'
          }}
          component="label"
        >
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept="image/*"
            onChange={onUploadImage}
          />
          <UploadIcon color="action" />
          <Typography variant="caption" align="center">
            Upload Image
          </Typography>
        </Paper>
        
        <Paper
          sx={{
            width: 100,
            height: 100,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: '2px dashed',
            borderColor: 'divider'
          }}
          onClick={onOpenCamera}
        >
          <CameraIcon color="action" />
          <Typography variant="caption" align="center">
            Use Camera
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default MacroImageGallery;
